/**
 * Phase 3a — Passkey API 래퍼 (Refs: #1112, Epic #1110).
 *
 * 백엔드 endpoint 10개를 safeboxClient (Authorization: JWT 자동) 호출로 매핑.
 * 응답 패턴: {code, message, data?} — code===200이면 정상, 그 외는 에러 코드.
 */
import { safeboxClient } from 'myNet'

// ---------------------------------------------------------------------------
// E1: POST /v1/auth/passkey/register-options
// ---------------------------------------------------------------------------
export async function fetchRegisterOptions() {
  const res = await safeboxClient.post('/v1/auth/passkey/register-options', {})
  return res.data
}

// ---------------------------------------------------------------------------
// E2: POST /v1/auth/passkey/register
// ---------------------------------------------------------------------------
export async function postRegister(body) {
  // body: { challenge_id, credential_response, prf_salt_echo, wrapped_umk_b64, iv_b64, algorithm, device_label }
  const res = await safeboxClient.post('/v1/auth/passkey/register', body)
  return res.data
}

// ---------------------------------------------------------------------------
// E3: GET /v1/auth/passkey/has-credentials
// ---------------------------------------------------------------------------
export async function getHasCredentials() {
  const res = await safeboxClient.get('/v1/auth/passkey/has-credentials')
  return res.data
}

// E4(login-options) / E5(login) 제거 — DBE Keeper(#1291) PRF 폐기. 패스키는 신원 검증 전용,
// 자동 잠금해제는 디바이스 키 흐름(api/authDevice.js)이 담당. 백엔드 엔드포인트도 PR-A 에서 제거됨.

// ---------------------------------------------------------------------------
// E6: GET /v1/auth/passkey/credentials
// ---------------------------------------------------------------------------
export async function fetchCredentials() {
  const res = await safeboxClient.get('/v1/auth/passkey/credentials')
  return res.data
}

// ---------------------------------------------------------------------------
// E7: DELETE /v1/auth/passkey/credentials/{id}
// ---------------------------------------------------------------------------
export async function deleteCredential(credentialId) {
  const res = await safeboxClient.delete(`/v1/auth/passkey/credentials/${credentialId}`)
  return res.data
}

// ---------------------------------------------------------------------------
// E8: PATCH /v1/auth/passkey/credentials/{id}
// ---------------------------------------------------------------------------
export async function patchCredentialLabel(credentialId, deviceLabel) {
  const res = await safeboxClient.patch(`/v1/auth/passkey/credentials/${credentialId}`, {
    device_label: deviceLabel,
  })
  return res.data
}

// E10(update wrapped_umk) 제거 — DBE Keeper(#1291): UMK wrap 은 디바이스 키 흐름이 담당하고,
// MPW 변경/복구 시 서버가 디바이스를 일괄 revoke 하므로 클라이언트 재봉인 엔드포인트 불필요.
// 백엔드 엔드포인트도 PR-A 에서 제거됨.

// ---------------------------------------------------------------------------
// E9: POST /v1/auth/verify-master-pw (credential 삭제 직전 본인 인증)
// ---------------------------------------------------------------------------
export async function postVerifyMasterPw(hmk) {
  const res = await safeboxClient.post('/v1/auth/verify-master-pw', { hmk })
  return res.data
}

// ---------------------------------------------------------------------------
// Telemetry: POST /v1/auth/passkey/telemetry/silent-skip (Phase 3a hotfix)
// best-effort — 실패해도 로그인 흐름 차단 금지 (try/catch swallow)
// ---------------------------------------------------------------------------
export async function postSilentSkipTelemetry(body) {
  // body: { aaguid, platform, browser, os_version, reason }
  try {
    await safeboxClient.post('/v1/auth/passkey/telemetry/silent-skip', body)
  } catch {
    // swallow — 텔레메트리 실패가 로그인 흐름 차단 금지
  }
}

// ---------------------------------------------------------------------------
// Telemetry: POST /v1/auth/passkey/telemetry/manual-retry (Phase 3a #1190 후속)
// 비호환 silent skip 분기에서 사용자가 수동 [Passkey 다시 시도] 클릭 시 결과 추적.
// sync 활성화 사용자 회복률 + curiosity click false-positive 비율 운영 가시성.
// best-effort — 실패해도 로그인 흐름 차단 금지 (try/catch swallow)
// ---------------------------------------------------------------------------
export async function postManualRetryTelemetry(body) {
  // body: { aaguid, platform, browser, os_version, result: 'success'|'failure', error_name: string|null }
  try {
    await safeboxClient.post('/v1/auth/passkey/telemetry/manual-retry', body)
  } catch {
    // swallow
  }
}
